// Covenant Calibration — the shared per-card confirmation footer used by
// all three capture cards (calibration / seasonality / banking). View mode
// shows "Confirmed by {name} on {date}" (or a muted "Not confirmed" hint)
// plus a "Mark confirmed" action; the action is hidden while the card is in
// edit mode and only rendered for Loans.Manage holders. Capture-only — no
// calculations; the server stamps confirmedBy/confirmedAt and the hook
// refetch updates the caption.

import { Badge, Button } from "@/design-system/components/ui";
import { formatDate } from "../common/colorBand";
import type { CovenantCardConfirmationDto } from "@/api/portfolioAdmin/types";

interface CovenantConfirmRowProps {
  confirmation: CovenantCardConfirmationDto;
  canEdit: boolean;
  /** Hide "Mark confirmed" while the card is in edit mode. */
  editing: boolean;
  onConfirm: () => void;
  confirming: boolean;
  testIdPrefix: string;
}

export function CovenantConfirmRow({
  confirmation,
  canEdit,
  editing,
  onConfirm,
  confirming,
  testIdPrefix,
}: CovenantConfirmRowProps) {
  return (
    <div className="mt-space-4 flex items-center justify-between gap-space-3 border-t border-border-light pt-space-3">
      {confirmation.confirmed ? (
        <div
          className="flex items-center gap-space-2 text-caption text-gray-mid"
          data-testid={`${testIdPrefix}-confirmed`}
        >
          <Badge status="finalized">Confirmed</Badge>
          <span>
            Confirmed by {confirmation.confirmedByName ?? "Unknown user"}
            {confirmation.confirmedAt
              ? ` on ${formatDate(confirmation.confirmedAt)}`
              : ""}
          </span>
        </div>
      ) : (
        <span
          className="text-caption text-status-warning"
          data-testid={`${testIdPrefix}-unconfirmed`}
        >
          Not confirmed
        </span>
      )}

      {canEdit && !editing && !confirmation.confirmed && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onConfirm}
          loading={confirming}
          data-testid={`${testIdPrefix}-confirm`}
        >
          Mark confirmed
        </Button>
      )}
    </div>
  );
}
